import { ApiBase } from "@/api/ApiBase";

export interface ParceiroParaDocumento {
  id: number;
  nome: string;
  nomeNif: string;
  nif: string;
  endereco: string | null;
  telefone: string | null;
}

export interface ArtigoParaDocumento {
  id: number;
  tipo: "P" | "S";
  nome: string;
  codigo: string;
  preco: number;
  retencao: "Sujeito" | "NA";
  taxa: number;
  siglaTaxa: string;
  qtdStock: number;
  nomeCodigo: string;
}

export type TipoDesconto = "Percentagem" | "Akz";

export interface ArtigoDocumentoPayload {
  artigo: { codigo: string };
  nome: string;
  precoUnitario: number;
  qtd: number;
  desconto: number;
  tipoDesconto: TipoDesconto;
}

export interface GuiaPayload {
  motorista: string;
  nomeVeiculo: string;
  matriculaVeiculo: string;
  dataCarga: string;
  dataDescarga: string;
  enderecoCarga: string;
  cidadeCarga: string;
  codigoPostalCarga: string;
  enderecoEntrega: string;
  cidadeEntrega: string;
  codigoPostalEntrega: string;
}

export type CanalDocumento = "Backoffice" | "POS";

export interface DocumentoPayload {
  tipo: string;
  canal: CanalDocumento;
  estabelecimento: { id: number };
  cliente: { id: number };
  descontoGlobal: number;
  tipoDescontoGlobal: TipoDesconto;
  retencaoFonte: number;
  dataVencimento: string | null;
  referencia: string;
  obs: string;
  valorEntregueDinheiro: number;
  valorEntregueBanco: number;
  contaBancaria: { id: number } | null;
  viaBancaria: string | null;
}

export interface CriarDocumentoResponse {
  code: number;
  msg: string;
  status: "Sucesso" | "Falha";
  data: string | null;
}

export type GrupoDocumento = "facturas" | "recibos" | "notas" | "guias";

export interface DocumentoResumo {
  data: string;
  hora: string;
  tipo: string;
  codigo: string;
  nomeCliente: string;
  numero: string;
  valorLiquido: number;
  estadoValidacao?: string;
}

export interface DocumentoDetalhe {
  id: number;
  codigo: string;
  numero: string;
  tipo: string;
  nomeCliente: string;
  nifCliente: string;
  canal: string;
  estado: string;
  feEstadoValidacao: string;
  feMsgValidacao: string | null;
  valorSubtotal: number;
  valorTotalImposto: number;
  valorTotalDesconto: number;
  valorTotalRetencao: number;
  valorTotal: number;
  descontoGlobal: number;
  tipoDescontoGlobal: TipoDesconto;
  retencaoFonte: number;
  totalGeralLiquidacaoFactura: number;
  totalGeralDevolvidoFactura: number;
  valorPorPagarFactura: number;
  dataCriacao: string;
  estabelecimento: { id: number; nome: string };
  criador: { id: number; nome: string };
}

export interface DocumentoComRelacionados {
  documento: DocumentoDetalhe | null;
  documentosRelacionados: DocumentoResumo[];
  qrBase64: string | null;
}

export interface ArtigoDocumentoLinha {
  id: number;
  nome: string;
  codigo: string;
  precoUnitario: number;
  precoCustoUnitario: number;
  subtotal: number;
  total: number;
  desconto: number;
  tipoDesconto: TipoDesconto;
  valorDescontoTotal: number;
  valorTaxa: number;
  taxa: number;
  tipo: "P" | "S";
  unidadeMedida: string;
  qtd: number;
  motivoIsencao: string | null;
  codigoImposto: string;
  nomeImposto: string;
  siglaImposto: string;
  descricaoDesconto: string | null;
  retencao: "Sujeito" | "NA";
  stock: "Sujeito" | "NA";
  codigoGrupoArtigo: string;
  artigo: { id: number; codigo: string };
  documento: { id: number; numero: string };
}

export interface NotaCreditoPayload {
  motivo: "RTF" | "ANL";
  descricao: string;
}

export interface DocumentoTransformacaoPayload {
  tipoDescontoGlobal: TipoDesconto;
  descontoGlobal: number;
  canal: CanalDocumento;
  referencia: string;
  obs: string;
  dataVencimento: string | null;
}

export interface ReciboPayload {
  id: number;
  canal: CanalDocumento;
  obs: string;
  viaBancaria: string | null;
  valorEntregueDinheiro: number;
  valorEntregueBanco: number;
  contaBancaria: { id: number } | null;
}

export interface DocumentoOrigemPOS {
  id: number;
  numero: string;
  tipo: string;
  dataCriacao: string;
  nomeCliente: string;
  nifCliente: string;
  nomeUtilizador: string;
  valorTotal: number;
  totalGeralLiquidacaoFactura: number;
  valorPorPagarFactura: number;
  descontoGlobal: number;
  tipoDescontoGlobal: TipoDesconto;
  retencaoFonte: number;
}

export interface ConsultaDocRCouNCResponse {
  documento: DocumentoOrigemPOS | null;
  artigos: ArtigoDocumentoLinha[];
  artigosNC: ArtigoDocumentoLinha[];
  msg: string;
  tipo: "sucesso" | "erro";
}

export interface VendaPOSResumo {
  data: string;
  hora: string;
  tipo: string;
  codigo: string;
  nomeCliente: string;
  numero: string;
  valorLiquido: number;
  nomeEstabelecimento: string;
  numeroEstabelecimento: string;
  nomeCaixa: string;
  estadoValidacao: string;
}

export interface EssencialMinhasVendasPOS {
  vendasHoje: number;
  totalVendasHoje: number;
  recibosHoje: number;
  totalRecibosHoje: number;
  notasHoje: number;
  totalNotasHoje: number;
  propostasHoje: number;
  totalPropostasHoje: number;
}

export class DocumentosApi extends ApiBase {
  listarPorGrupoPaginada(
    grupo: GrupoDocumento,
    pagina: number,
  ): Promise<DocumentoResumo[]> {
    return this.http.put<DocumentoResumo[]>(
      `/api/facturacao/documento/lista-by-comerciante/${grupo}/${pagina}`,
    );
  }

  getOneByCodigo(codigo: string): Promise<DocumentoComRelacionados> {
    return this.http.put<DocumentoComRelacionados>(
      `/api/facturacao/documento/get-one-by-codigo/${codigo}`,
    );
  }

  listarParceiros(pagina: number): Promise<ParceiroParaDocumento[]> {
    return this.http.put<ParceiroParaDocumento[]>(
      `/api/facturacao/parceiro/lista-by-comerciante-parceiros-to-doc/${pagina}`,
    );
  }

  listarArtigos(
    estabelecimentoId: number,
    pagina: number,
  ): Promise<ArtigoParaDocumento[]> {
    return this.http.put<ArtigoParaDocumento[]>(
      `/api/facturacao/artigo/lista-by-comerciante-to-doc/backoffice/${estabelecimentoId}/${pagina}`,
    );
  }

  criarDocumento(
    documento: DocumentoPayload,
    artigos: ArtigoDocumentoPayload[],
    guia: GuiaPayload | null,
    comprovativo: File | null,
  ): Promise<CriarDocumentoResponse> {
    const infoDoc = { documento, artigos, guia };
    const fd = new FormData();
    fd.append(
      "infoDoc",
      new Blob([JSON.stringify(infoDoc)], { type: "application/json" }),
    );
    if (comprovativo) fd.append("fileComprovativo", comprovativo);

    // 60s (não os 30s por defeito): quando é o 1º documento de um tipo/série, o backend pode
    // ter de criar a série junto da AGT E submeter o documento — duas chamadas externas
    // sequenciais, cada uma com até 60s de timeout do lado do Java. Com 30s aqui, o Vue
    // mostrava "erro de servidor" antes do backend terminar, mesmo quando o documento acabava
    // por ser emitido com sucesso.
    return this.http.postMultipart<CriarDocumentoResponse>(
      "/api/facturacao/documento/add",
      fd,
      60000,
    );
  }

  artigosNaoUsadosEmNC(codigo: string): Promise<ArtigoDocumentoLinha[]> {
    return this.http.put<ArtigoDocumentoLinha[]>(
      `/api/facturacao/documento/artigos-nao-usados-em-nc/${codigo}`,
    );
  }

  artigosDocumentoProposta(codigo: string): Promise<ArtigoDocumentoLinha[]> {
    return this.http.put<ArtigoDocumentoLinha[]>(
      `/api/facturacao/documento/artigos-documento-proposta/${codigo}`,
    );
  }

  emitirNotaCredito(
    documento: DocumentoTransformacaoPayload,
    artigos: ArtigoDocumentoPayload[],
    notaCredito: NotaCreditoPayload,
    idFTorFRtoNC: number,
  ): Promise<CriarDocumentoResponse> {
    const infoDoc = { documento, artigos, notaCredito, idFTorFRtoNC };
    const fd = new FormData();
    fd.append(
      "infoDoc",
      new Blob([JSON.stringify(infoDoc)], { type: "application/json" }),
    );
    // 60s — ver comentário em criarDocumento() acima (série nova pode exigir 2 chamadas à AGT).
    return this.http.postMultipart<CriarDocumentoResponse>(
      "/api/facturacao/documento/add-nota-de-credito",
      fd,
      60000,
    );
  }

  emitirFacturaReferenteProposta(
    documento: DocumentoTransformacaoPayload,
    artigos: ArtigoDocumentoPayload[],
    idPPorORtoNC: number,
  ): Promise<CriarDocumentoResponse> {
    const infoDoc = { documento, artigos, idPPorORtoNC };
    const fd = new FormData();
    fd.append(
      "infoDoc",
      new Blob([JSON.stringify(infoDoc)], { type: "application/json" }),
    );
    // 60s — ver comentário em criarDocumento() acima (série nova pode exigir 2 chamadas à AGT).
    return this.http.postMultipart<CriarDocumentoResponse>(
      "/api/facturacao/documento/add-factura-referente",
      fd,
      60000,
    );
  }

  emitirRecibo(
    dados: ReciboPayload,
    comprovativo: File | null,
  ): Promise<CriarDocumentoResponse> {
    const fd = new FormData();
    fd.append(
      "infoDoc",
      new Blob([JSON.stringify(dados)], { type: "application/json" }),
    );
    if (comprovativo) fd.append("fileComprovativo", comprovativo);
    // 60s — ver comentário em criarDocumento() acima (série nova pode exigir 2 chamadas à AGT).
    return this.http.postMultipart<CriarDocumentoResponse>(
      "/api/facturacao/documento/add-recibo",
      fd,
      60000,
    );
  }

  consultaDocParaRCouNC(
    tipo: "RC" | "NC",
    numeroDoc: string,
  ): Promise<ConsultaDocRCouNCResponse> {
    return this.http.post<ConsultaDocRCouNCResponse>(
      "/api/facturacao/documento/consulta-doc-to-rc-or-nc",
      { tipo, numeroDoc },
    );
  }

  listaVendasPOS(
    grupo: "facturas" | "recibos" | "notas" | "propostas",
    pagina: number,
  ): Promise<VendaPOSResumo[]> {
    return this.http.put<VendaPOSResumo[]>(
      `/api/facturacao/documento/lista-by-comerciante-and-vendedor-pos/${grupo}/${pagina}`,
    );
  }

  essencialToMinhasVendasPOS(): Promise<EssencialMinhasVendasPOS> {
    return this.http.put<EssencialMinhasVendasPOS>(
      "/api/facturacao/documento/essencial-to-minhas-vendas-pos",
    );
  }
}
